/**
 * /goals/$goalId — single goal detail.
 *
 * Layout:
 *   1. Breadcrumb: parent → current
 *   2. Stat block: progress bar, status, owner, deadline
 *   3. KPI table (key/target/current/unit)
 *   4. Linked projects + boards (just IDs for now — Phase 12 will resolve names)
 *   5. Sub-goals tree (uses GoalsTree, scoped to direct children)
 */
import type { Goal } from '@chaos/types/constitution';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Loader2, Target } from 'lucide-react';

import { GoalsTree } from '../../../components/constitution/goals-tree';
import { constitutionApi, constitutionKeys } from '../../../lib/api/constitution';
import { cn } from '../../../lib/utils';

function GoalDetailRoute() {
  const params = useParams({ from: '/_authenticated/goals/$goalId' });
  const goalId = params.goalId;

  const goalQ = useQuery({
    queryKey: constitutionKeys.goal(goalId),
    queryFn: () => constitutionApi.getGoal(goalId),
    staleTime: 15_000,
  });
  const childrenQ = useQuery({
    queryKey: constitutionKeys.goalChildren(goalId),
    queryFn: () => constitutionApi.listGoalChildren(goalId),
    staleTime: 30_000,
  });
  const parentQ = useQuery({
    queryKey: constitutionKeys.goal(goalQ.data?.parentGoalId ?? ''),
    queryFn: () =>
      goalQ.data?.parentGoalId ? constitutionApi.getGoal(goalQ.data.parentGoalId) : null,
    enabled: !!goalQ.data?.parentGoalId,
    staleTime: 30_000,
  });

  if (goalQ.isLoading) {
    return (
      <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
        <p className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading goal…
        </p>
      </section>
    );
  }
  if (!goalQ.data) {
    return (
      <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
        <p className="text-[12px] text-muted-foreground">Goal not found.</p>
        <Link to="/goals" className="text-[12px] text-foreground underline">
          ← Back to goals
        </Link>
      </section>
    );
  }
  const goal = goalQ.data;

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
      <header className="flex flex-wrap items-baseline gap-2">
        <Link
          to="/goals"
          className="inline-flex items-center gap-0.5 text-[12px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Goals
        </Link>
        {parentQ.data ? (
          <>
            <ChevronRight className="h-3 w-3 text-muted-foreground/40" aria-hidden />
            <Link
              to="/goals/$goalId"
              params={{ goalId: parentQ.data.id }}
              className="text-[12px] text-muted-foreground hover:text-foreground"
            >
              {parentQ.data.statement.length > 40
                ? `${parentQ.data.statement.slice(0, 40)}…`
                : parentQ.data.statement}
            </Link>
          </>
        ) : null}
      </header>

      <div className="flex items-baseline gap-3">
        <Target className="h-6 w-6 shrink-0 text-foreground" aria-hidden />
        <div className="flex flex-col">
          <h1 className="text-[18px] font-semibold tracking-tight text-foreground">
            {goal.statement}
          </h1>
          <span className="font-mono text-[11px] text-muted-foreground">{goal.goalKey}</span>
        </div>
      </div>

      <ProgressBlock goal={goal} />

      <KpiBlock goal={goal} />

      <LinksBlock goal={goal} />

      <section className="flex flex-col gap-2">
        <header className="flex items-center justify-between border-b border-border pb-1">
          <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Sub-goals
          </h2>
          <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70">
            {childrenQ.data?.goals.length ?? 0}
          </span>
        </header>
        {childrenQ.isLoading ? (
          <p className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading children…
          </p>
        ) : (
          <GoalsTree goals={childrenQ.data?.goals ?? []} rootId={goalId} />
        )}
      </section>
    </section>
  );
}

function ProgressBlock({ goal }: { goal: Goal }) {
  const progress = goal.progress ?? 0;
  const pct = Math.round(progress * 100);
  let cls = 'bg-success';
  if (progress < 0.34) cls = 'bg-destructive';
  else if (progress < 0.67) cls = 'bg-warning';
  return (
    <article className="rounded border border-border bg-background p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Progress
        </h2>
        <span className="font-mono text-[12px] tabular-nums text-foreground">
          {goal.progress == null ? '—' : `${pct}%`}
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full transition-all', cls)}
          style={{ width: goal.progress == null ? '0%' : `${pct}%` }}
        />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
        <Pair label="Status" value={goal.status} />
        <Pair label="Deadline" value={goal.deadline ? goal.deadline.slice(0, 10) : '—'} />
        <Pair
          label="Target"
          value={
            goal.targetValue != null
              ? `${goal.targetValue}${goal.unit ? ` ${goal.unit}` : ''}`
              : '—'
          }
        />
        <Pair
          label="Current"
          value={
            goal.currentValue != null
              ? `${goal.currentValue}${goal.unit ? ` ${goal.unit}` : ''}`
              : '—'
          }
        />
        <Pair label="Children" value={String(goal.childCount)} />
        <Pair label="Owner" value={goal.ownerHumanId ?? '—'} />
      </dl>
    </article>
  );
}

function KpiBlock({ goal }: { goal: Goal }) {
  const entries = Object.entries(goal.kpi ?? {});
  if (entries.length === 0) return null;
  return (
    <article className="rounded border border-border bg-background p-4">
      <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        KPI
      </h2>
      <ul className="mt-2 divide-y divide-border text-[12px]">
        {entries.map(([k, v]) => (
          <li key={k} className="flex items-baseline justify-between py-1">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-mono tabular-nums text-foreground">
              {typeof v === 'object' ? JSON.stringify(v) : String(v)}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function LinksBlock({ goal }: { goal: Goal }) {
  if (goal.linkedProjectIds.length === 0 && goal.linkedBoardIds.length === 0) return null;
  return (
    <article className="rounded border border-border bg-background p-4 text-[12px]">
      <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Linked
      </h2>
      <dl className="mt-2 grid grid-cols-2 gap-3">
        {goal.linkedProjectIds.length > 0 ? (
          <div>
            <dt className="text-muted-foreground">Projects</dt>
            <dd>
              <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[10px]">
                {goal.linkedProjectIds.map((id) => (
                  <li key={id} className="truncate text-muted-foreground/80">
                    {id}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ) : null}
        {goal.linkedBoardIds.length > 0 ? (
          <div>
            <dt className="text-muted-foreground">Boards</dt>
            <dd>
              <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[10px]">
                {goal.linkedBoardIds.map((id) => (
                  <li key={id} className="truncate text-muted-foreground/80">
                    {id}
                  </li>
                ))}
              </ul>
            </dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}

function Pair({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/goals/$goalId')({
  component: GoalDetailRoute,
});
